import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import RootLayout from '../componentes/navegacion/RootLayout';
import AppNavbar from '../componentes/navegacion/AppNavbar';
import ProtectedRoute from '../componentes/navegacion/ProtectedRoute';
import PageLoader from '../componentes/common/PageLoader';
import NotFound from '../componentes/common/NotFound';
import Unauthorized from '../componentes/common/Unauthorized';

// ─── Auth (NOT lazy — needed immediately on first load) ──────────────────────
import Login from '../componentes/seguridad/Login';
import LandingPage from '../componentes/landing/LandingPage';

// ─── All protected modules loaded LAZILY ─────────────────────────────────────
const Dashboard       = lazy(() => import('../componentes/dashboard/Dashboard'));
const PerfilUsuario   = lazy(() => import('../componentes/seguridad/PerfilUsuario'));
const ListUsers       = lazy(() => import('../componentes/users/ListUsers'));

// Admin / Secretary / SuperAdmin
const NewAssociate           = lazy(() => import('../componentes/associate/NewAssociate'));
const TableAssociate         = lazy(() => import('../componentes/associate/ListAssociate'));
const AssociateChangeLogPage = lazy(() => import('../componentes/associate/AssociateChangeLogPage'));
const TableMeasurer   = lazy(() => import('../componentes/Measurer/ListTableMeasurer'));
const TableClassification = lazy(() => import('../componentes/Classification/ListClassification'));
const TableContribution   = lazy(() => import('../componentes/Contribution/ListContribution'));
const TableFine           = lazy(() => import('../componentes/fine/ListFine'));
const TableConsumption    = lazy(() => import('../componentes/consumption/ListCardMeasurer'));
const TableContributionAdd = lazy(() => import('../componentes/Contribution/ListCardMeasurer'));
const TableFineAdd        = lazy(() => import('../componentes/fine/ListCardMeasurer'));
const MainTransferred     = lazy(() => import('../componentes/transferred/TransferStepper'));
const PassiveAdd          = lazy(() => import('../componentes/passive/Add'));
const ConsumptionClean    = lazy(() => import('../componentes/consumption/Clean'));
const AmnestyList         = lazy(() => import('../componentes/amnesty/AmnestyList'));
const TableInstitution    = lazy(() => import('../componentes/institution/ListInstitution'));

// Accounting
const TableEntry    = lazy(() => import('../componentes/Entry/EntryList'));
const NewEntry      = lazy(() => import('../componentes/Entry/NewEntry'));
const NewExpense    = lazy(() => import('../componentes/Expense/NewExpense'));

// Reports
const Paytoday          = lazy(() => import('../componentes/Report/Paytoday'));
const MeasurerReader    = lazy(() => import('../componentes/Report/MeasurerReader'));
const MeasurerCut       = lazy(() => import('../componentes/Report/MeasurerCut'));
const MeasurerPay       = lazy(() => import('../componentes/Report/MeasurerPay'));
const TransferAssociate = lazy(() => import('../componentes/Report/TransferAssociate'));
const Measures          = lazy(() => import('../componentes/Report/Measurers'));
const Charges           = lazy(() => import('../componentes/Report/Charges'));
const Consumptions      = lazy(() => import('../componentes/Report/Consumptions'));

// Attendance
const BarcodeSearch     = lazy(() => import('../componentes/attendance/BarcodeReader'));
const EventRegister     = lazy(() => import('../componentes/attendance/EventRegister'));
const AttendanceRegister = lazy(() => import('../componentes/attendance/AttendanceRegister'));
const Administration    = lazy(() => import('../componentes/attendance/Administration'));
const LastAttendance    = lazy(() => import('../componentes/attendance/LastAttendance'));

// Role-specific
const RoleReaderList       = lazy(() => import('../componentes/RoleReader/ListCardMeasurer'));
const RoleAssociateDashboard = lazy(() => import('../componentes/RoleAssociate/AssociateDashboard'));
const RoleAssociateIndex     = lazy(() => import('../componentes/RoleAssociate/AssociateDebts'));
const RoleAssociateIndexPay  = lazy(() => import('../componentes/RoleAssociate/AssociatePayments'));

// Billing configuration
const BillingConfigurationSettings = lazy(() => import('../componentes/BillingConfiguration/BillingConfigurationSettings'));

// Void management
const VoidManagement = lazy(() => import('../componentes/ConsumptionVoid/VoidManagement'));

// Payment flow
const PaymentMethodConfig    = lazy(() => import('../componentes/paymentMethod/PaymentMethodConfig'));
const AdminPaymentRequests   = lazy(() => import('../componentes/paymentRequest/AdminPaymentRequests'));
const AssociatePaymentRequests = lazy(() => import('../componentes/paymentRequest/AssociatePaymentRequests'));

// Other
const NewRoles  = lazy(() => import('../componentes/Roles/NewRoles'));
const NewNotice = lazy(() => import('../componentes/Notice/NewNotice'));

// ─── Lazy Suspense wrapper ───────────────────────────────────────────────────
const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// ─── Protected + Lazy wrapper ────────────────────────────────────────────────
const PrivateLazy = ({ component: Component, requiredRoles = [] }) => (
  <ProtectedRoute requiredRoles={requiredRoles}>
    <LazyRoute>
      <Component />
    </LazyRoute>
  </ProtectedRoute>
);

// ─── Role groups ─────────────────────────────────────────────────────────────
const ADMIN_ROLES     = ['SuperAdmin', 'Admin', 'Secretary'];
const ADMIN_DIRECTIVO = ['SuperAdmin', 'Admin', 'Secretary', 'Directivo'];
const ACCOUNTING      = ['SuperAdmin', 'Admin', 'Tesorero'];
const REPORTS         = ['SuperAdmin', 'Admin', 'Tesorero', 'Directivo'];
const ATTENDANCE      = ['SuperAdmin', 'Admin', 'Secretary', 'Directivo'];

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      // ── Public routes ──────────────────────────────────────────────────────
      { index: true, element: <LandingPage /> },
      { path: "auth/login", element: <Login /> },
      { path: "unauthorized", element: <Unauthorized /> },

      // ── Protected routes (inside the main layout) ──────────────────────────
      {
        element: <AppNavbar />,
        children: [
          // Dashboard & Profile — any authenticated user
          { path: "dashboard", element: <PrivateLazy component={Dashboard} /> },
          { path: "auth/perfil", element: <PrivateLazy component={PerfilUsuario} /> },

          // User management — SuperAdmin only
          { path: "users/list", element: <PrivateLazy component={ListUsers} requiredRoles={['SuperAdmin']} /> },

          // Roles admin — SuperAdmin only
          { path: "roles/new", element: <PrivateLazy component={NewRoles} requiredRoles={['SuperAdmin']} /> },

          // Associate
          { path: "associate/new",       element: <PrivateLazy component={NewAssociate}           requiredRoles={ADMIN_ROLES} /> },
          { path: "associate/list",      element: <PrivateLazy component={TableAssociate}          requiredRoles={ADMIN_DIRECTIVO} /> },
          { path: "associate/changelog", element: <PrivateLazy component={AssociateChangeLogPage} requiredRoles={['SuperAdmin', 'Admin']} /> },

          // Measurer — Secretary excluded
          { path: "measurer/list", element: <PrivateLazy component={TableMeasurer} requiredRoles={['SuperAdmin', 'Admin', 'Directivo']} /> },

          // Config
          { path: "institution/list",   element: <PrivateLazy component={TableInstitution}    requiredRoles={['SuperAdmin', 'Admin', 'Directivo']} /> },
          { path: "classification/list",element: <PrivateLazy component={TableClassification} requiredRoles={['SuperAdmin', 'Admin', 'Directivo']} /> },
          { path: "contribution/list",  element: <PrivateLazy component={TableContribution}   requiredRoles={ADMIN_DIRECTIVO} /> },
          { path: "fine/list",          element: <PrivateLazy component={TableFine}            requiredRoles={ADMIN_DIRECTIVO} /> },
          { path: "amnesty/list",       element: <PrivateLazy component={AmnestyList}          requiredRoles={ADMIN_DIRECTIVO} /> },
          { path: "billing/config",     element: <PrivateLazy component={BillingConfigurationSettings} requiredRoles={['SuperAdmin', 'Admin']} /> },
          { path: "consumptions/void",  element: <PrivateLazy component={VoidManagement} requiredRoles={['SuperAdmin']} /> },

          // Administration
          { path: "consumption/list",    element: <PrivateLazy component={TableConsumption}    requiredRoles={ADMIN_DIRECTIVO} /> },
          { path: "contributionAdd/list",element: <PrivateLazy component={TableContributionAdd} requiredRoles={ADMIN_ROLES} /> },
          { path: "fineAdd/list",        element: <PrivateLazy component={TableFineAdd}         requiredRoles={ADMIN_ROLES} /> },
          { path: "transferred/register",element: <PrivateLazy component={MainTransferred}      requiredRoles={['SuperAdmin', 'Admin']} /> },
          { path: "passive/add",         element: <PrivateLazy component={PassiveAdd}           requiredRoles={['SuperAdmin', 'Admin']} /> },
          { path: "clean/error",         element: <PrivateLazy component={ConsumptionClean}     requiredRoles={['SuperAdmin', 'Admin']} /> },

          // Accounting
          { path: "accounting",             element: <PrivateLazy component={TableEntry}  requiredRoles={ACCOUNTING} /> },
          { path: "accounting/entries/new", element: <PrivateLazy component={NewEntry}    requiredRoles={ACCOUNTING} /> },
          { path: "accounting/expenses/new",element: <PrivateLazy component={NewExpense}  requiredRoles={ACCOUNTING} /> },

          // Reports
          { path: "reports/paytoday",        element: <PrivateLazy component={Paytoday}          requiredRoles={REPORTS} /> },
          { path: "reports/measurer",        element: <PrivateLazy component={MeasurerReader}     requiredRoles={[...REPORTS, 'Reader']} /> },
          { path: "reports/measurerCut",     element: <PrivateLazy component={MeasurerCut}        requiredRoles={REPORTS} /> },
          { path: "reports/measurerPay",     element: <PrivateLazy component={MeasurerPay}        requiredRoles={REPORTS} /> },
          { path: "reports/transferAssociate",element:<PrivateLazy component={TransferAssociate}  requiredRoles={REPORTS} /> },
          { path: "reports/measures",        element: <PrivateLazy component={Measures}           requiredRoles={REPORTS} /> },
          { path: "reports/charges",         element: <PrivateLazy component={Charges}            requiredRoles={REPORTS} /> },
          { path: "reports/consumptions",    element: <PrivateLazy component={Consumptions}       requiredRoles={REPORTS} /> },

          // Attendance
          { path: "attendance/events",   element: <PrivateLazy component={EventRegister}      requiredRoles={ATTENDANCE} /> },
          { path: "attendance/register", element: <PrivateLazy component={AttendanceRegister}  requiredRoles={ATTENDANCE} /> },
          { path: "attendance/control",  element: <PrivateLazy component={BarcodeSearch}       requiredRoles={ATTENDANCE} /> },
          { path: "attendance/admin",    element: <PrivateLazy component={Administration}      requiredRoles={ATTENDANCE} /> },
          { path: "attendance/last",     element: <PrivateLazy component={LastAttendance}      requiredRoles={ATTENDANCE} /> },

          // Role Reader
          { path: "roleReader/list", element: <PrivateLazy component={RoleReaderList} requiredRoles={['SuperAdmin', 'Reader']} /> },

          // Role Associate
          // Associate panel — new dashboard + detail routes
          { path: "roleAssociate/dashboard",                            element: <PrivateLazy component={RoleAssociateDashboard} requiredRoles={['Associate']} /> },
          { path: "roleAssociate/measurer/:measurerId/debts",           element: <PrivateLazy component={RoleAssociateIndex}     requiredRoles={['Associate']} /> },
          { path: "roleAssociate/measurer/:measurerId/pays",            element: <PrivateLazy component={RoleAssociateIndexPay}  requiredRoles={['Associate']} /> },
          // Legacy redirects — keep for backwards compatibility
          { path: "roleAssociate/list",     element: <Navigate to="/roleAssociate/dashboard" replace /> },
          { path: "roleAssociate/listPays", element: <Navigate to="/roleAssociate/dashboard" replace /> },

          // Payment flow — admin
          { path: "payment/methods",   element: <PrivateLazy component={PaymentMethodConfig}  requiredRoles={ADMIN_ROLES} /> },
          { path: "payment/requests",  element: <PrivateLazy component={AdminPaymentRequests} requiredRoles={ADMIN_ROLES} /> },

          // Payment flow — associate
          { path: "roleAssociate/payment-requests", element: <PrivateLazy component={AssociatePaymentRequests} requiredRoles={['Associate']} /> },

          // Notices
          { path: "notice/new", element: <PrivateLazy component={NewNotice} requiredRoles={ADMIN_ROLES} /> },

          // Legacy redirects
          { path: "reports",         element: <Navigate to="/accounting" replace /> },
          { path: "entry/new",       element: <Navigate to="/accounting/entries/new" replace /> },
          { path: "expense/new",     element: <Navigate to="/accounting/expenses/new" replace /> },
        ]
      },

      // ── 404 Catch-all ──────────────────────────────────────────────────────
      { path: "*", element: <NotFound /> },
    ]
  }
]);
